import {
  Box,
  Card,
  CardContent,
  IconButton,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import StartIcon from '@material-ui/icons/Star';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { ShortcutLink } from '../../types';

type Props = {
  link: ShortcutLink;
  isFavorite: boolean;
  toggleFavorite: (link: ShortcutLink) => void;
  onEdit?: (link: ShortcutLink) => void;
  onDelete?: (link: ShortcutLink) => void;
  actionsDisabled?: boolean;
};

export const ShortcutSectionItem: React.FC<Props> = ({
  link,
  isFavorite,
  toggleFavorite,
  onEdit,
  onDelete,
  actionsDisabled = false,
}) => {
  const classes = useStyles();

  const handleToggleFavorite = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(link);
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onEdit?.(link);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDelete?.(link);
  };

  return (
    <Link
      key={link.id}
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.compactLink}
    >
      <Card key={link.id} className={classes.compactCard}>
        <CardContent>
          <Box className={classes.compactLink}>
            <Box flex={1} minWidth={0}>
              <Typography className={classes.compactTitle}>
                {link.name}
              </Typography>
              <Typography className={classes.compactDescription}>
                {link.description}
              </Typography>
            </Box>
            <Box className={classes.compactIcon}>
              {link.isCustom && onEdit && (
                <IconButton
                  size="small"
                  edge="end"
                  onClick={handleEdit}
                  className={classes.actionButton}
                  aria-label="Edit custom shortcut"
                  disabled={actionsDisabled}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {link.isCustom && onDelete && (
                <IconButton
                  size="small"
                  edge="end"
                  onClick={handleDelete}
                  className={classes.actionButton}
                  aria-label="Delete custom shortcut"
                  disabled={actionsDisabled}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                edge="end"
                onClick={handleToggleFavorite}
                className={`${classes.actionButton} ${
                  isFavorite ? classes.actionButtonFavorite : ''
                }`}
                aria-label={
                  isFavorite ? 'Unfavorite shortcut' : 'Favorite shortcut'
                }
                disabled={actionsDisabled}
              >
                <StartIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Link>
  );
};

const useStyles = makeStyles(theme => ({
  compactCard: {
    borderRadius: 8,
    border: `1px solid ${theme.palette.border}`,
    boxShadow: 'none',
    minHeight: 78,
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.info.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  compactLink: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    color: 'inherit',
    height: '100%',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  compactIcon: {
    color: theme.palette.info.main,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
  compactTitle: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  compactDescription: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    fontSize: '0.84rem',
    lineHeight: 1.45,
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  actionButton: {
    color: theme.palette.text.secondary,
  },
  actionButtonFavorite: {
    color: theme.palette.primary.main,
  },
}));
